import { ChangeDetectionStrategy, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { combineLatest, Observable, of, ReplaySubject, map, startWith, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { CmsTemplate } from '@fcom/core-api';
import { CmsPageService, GaContext, LoaderType, ToasterService } from '@fcom/common';
import {
  FinnairAddressData,
  FinnairEmergencyContact,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairTravelRequirementsEligibility,
} from '@fcom/dapi/api/models';
import { CommonBookingFeatureState, orderData } from '@fcom/common-booking';
import { SubviewComponent } from '@fcom/common-booking/components/subview/subview.component';
import { LanguageService } from '@fcom/ui-translate';
import { ButtonComponent, ButtonTheme, ModalButtons } from '@fcom/ui-components';
import { finShare } from '@fcom/rx';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';

import { generateApisToasterMessageIdByType } from '../travel-preparations-utils';

@Component({
  selector: 'fin-travel-preparations-route',
  templateUrl: 'travel-preparations-route.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingTravelPreparationsRouteComponent implements OnInit {
  readonly LoaderType = LoaderType;
  readonly TRACKING_CONTEXT = GaContext.MANAGE_BOOKING;
  readonly ModalButtons: typeof ModalButtons = ModalButtons;
  readonly ButtonTheme = ButtonTheme;
  readonly subviewName = 'travel-preparations-subview';
  readonly SvgLibraryIcon = SvgLibraryIcon;

  unsavedChangesModalOpen = false;
  apisPaxFormDirtyStatus = false;
  order$: Observable<FinnairOrder>;
  template$: Observable<{ loading: boolean; data: CmsTemplate }>;
  selectedPassenger$: Observable<FinnairPassengerItem>;
  addresses$: Observable<Array<{ name: string; id: string; address: FinnairAddressData }>>;
  openFormForPaxId$: ReplaySubject<string> = new ReplaySubject<string>(1);
  requiredTravelRequirements$: Observable<string[]>;
  showEmergencyContact$: Observable<boolean>;
  emergencyContact$: Observable<FinnairEmergencyContact>;

  @ViewChild('subview', { static: false })
  subview: SubviewComponent;

  private elementToFocus: ButtonComponent;

  constructor(
    private cmsPageService: CmsPageService,
    private router: Router,
    private store$: Store<CommonBookingFeatureState>,
    private languageService: LanguageService,
    private toasterService: ToasterService,
    private feedbackService: FeedbackService
  ) {}

  ngOnInit(): void {
    this.order$ = this.store$.pipe(orderData());
    this.emergencyContact$ = this.order$.pipe(map((order) => order?.emergencyContact));
    this.selectedPassenger$ = this.openFormForPaxId$.pipe(
      switchMap((paxId) => {
        return this.order$.pipe(
          map((order) => {
            return order.passengers.find((passenger) => passenger.id === paxId);
          })
        );
      })
    );

    this.addresses$ = this.order$.pipe(
      switchMap((order) => {
        const items: Array<Observable<{ name: string; id: string; address: FinnairAddressData }>> =
          order.passengers.reduce((passengerItems, passenger) => {
            const address = passenger.advancePassengerInformation?.destinationAddress?.data;

            if (address && Object.values(address).some((value) => value !== '')) {
              passengerItems.push(
                this.languageService
                  .translate('paxDetails.travellingWith', {
                    ...passenger,
                    number: passengerItems.length + 1,
                  })
                  .pipe(map((name) => ({ name, id: passenger.id, address })))
              );
            }
            return passengerItems;
          }, []);
        if (items.length === 0) {
          return of([]);
        }
        return combineLatest(items);
      })
    );

    this.template$ = this.cmsPageService.getPageData(this.router.url).pipe(
      map((data) => ({ loading: false, data })),
      startWith({ loading: true, data: null }),
      finShare()
    );

    this.requiredTravelRequirements$ = this.order$.pipe(
      map((order) =>
        Object.entries(order.eligibilities.travelRequirement)
          .filter(([_, value]: [string, FinnairTravelRequirementsEligibility]) => value.required)
          .map(([key, _]: [string, FinnairTravelRequirementsEligibility]) => key)
      )
    );

    this.showEmergencyContact$ = this.order$.pipe(
      map((order) => order.eligibilities.travelRequirement?.emergencyContactDetails?.required ?? false)
    );
    this.feedbackService.setSurvey(QualtricsSurveys.MMB_FEEDBACK);
    this.feedbackService.setFeedbackWidgetVisibility(true);
  }

  openForm(openFormEvent: { passengerId: string; triggerElement: ButtonComponent }): void {
    this.openFormForPaxId$.next(openFormEvent.passengerId);
    this.elementToFocus = openFormEvent.triggerElement;
    this.toasterService.removeMessage(generateApisToasterMessageIdByType('success'));
    this.toasterService.removeMessage(generateApisToasterMessageIdByType('error'));
    this.subview.open = true;
  }

  onCloseSubview(): void {
    this.elementToFocus?.focus();
  }

  setApisPaxFormDirtyStatus(dirty: boolean): void {
    this.apisPaxFormDirtyStatus = dirty;
  }

  leaveWithoutSaving(): void {
    this.unsavedChangesModalOpen = false;
    this.apisPaxFormDirtyStatus = false;
    this.subview.open = false;
  }

  continueEditing(): void {
    this.unsavedChangesModalOpen = false;
  }

  closeSubview(): void {
    if (this.apisPaxFormDirtyStatus) {
      this.unsavedChangesModalOpen = true;
    } else {
      this.leaveWithoutSaving();
    }
  }

  @HostListener('window:beforeunload')
  canUnload(): boolean {
    return !this.apisPaxFormDirtyStatus;
  }
}
